/**
 * Layout engine — collision detection and placement logic for edit mode.
 *
 * Owns the spatial rules that board-ops.ts deliberately defers:
 * board-ops guarantees schema validity; this module guarantees visual correctness
 * (no overlapping tiles, valid grid bounds). Spatial memory is sacred
 * (core-flows §3, glance-grammar-spec §2.3): we never auto-rearrange.
 *
 * Immutability: every function is pure — returns new arrays/objects, never patches.
 */

import type { Board } from 'briefpane/schema';
import { sizeToWH } from 'briefpane/schema';

export interface GridCell {
  readonly x: number;
  readonly y: number;
}

export interface LayoutEntry {
  readonly tileId: string;
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

/**
 * Check whether placing a footprint {w,h} at {x,y} would overlap any existing
 * layout entry (excluding the tile being moved, identified by skipId).
 *
 * Pure: no side-effects, no mutations.
 */
export function hasCollision(
  layout: readonly LayoutEntry[],
  x: number,
  y: number,
  w: number,
  h: number,
  skipId: string | null,
): boolean {
  for (const entry of layout) {
    if (entry.tileId === skipId) continue;
    // Two rectangles overlap iff neither is fully to the left/right/above/below.
    const noOverlap =
      x + w <= entry.x ||   // candidate is left of entry
      x >= entry.x + entry.w || // candidate is right of entry
      y + h <= entry.y ||   // candidate is above entry
      y >= entry.y + entry.h;   // candidate is below entry
    if (!noOverlap) return true;
  }
  return false;
}

/**
 * Find the nearest non-colliding grid cell to {targetX, targetY} for a tile with
 * footprint {w, h}, scanning in a spiral-like nearest-first order.
 *
 * Returns null if no valid position found within the search range.
 *
 * Used for: collision recovery on drop (revert-or-nearest), placement on add.
 */
export function findNearestFreeCell(
  layout: readonly LayoutEntry[],
  targetX: number,
  targetY: number,
  w: number,
  h: number,
  skipId: string | null,
  gridCols: number,
): GridCell | null {
  const maxRows = 100; // practical bound; board layout wraps naturally
  // Scan in increasing distance from target (Manhattan-ish)
  for (let radius = 0; radius <= Math.max(gridCols, maxRows); radius++) {
    for (let dy = -radius; dy <= radius; dy++) {
      for (let dx = -radius; dx <= radius; dx++) {
        if (Math.abs(dx) !== radius && Math.abs(dy) !== radius) continue; // shell only
        const cx = targetX + dx;
        const cy = targetY + dy;
        if (cx < 0 || cy < 0 || cx + w > gridCols) continue;
        if (!hasCollision(layout, cx, cy, w, h, skipId)) {
          return { x: cx, y: cy };
        }
      }
    }
  }
  return null;
}

/**
 * Compute the grid column for a pointer position within the grid container.
 *
 * @param pointerX - pointer X relative to the grid container
 * @param cellWidth - width of one grid cell in pixels (including gap)
 * @param gridCols - total number of columns
 */
export function pointerToGridX(pointerX: number, cellWidth: number, gridCols: number): number {
  return Math.max(0, Math.min(gridCols - 1, Math.floor(pointerX / cellWidth)));
}

/**
 * Compute the grid row for a pointer position within the grid container.
 */
export function pointerToGridY(pointerY: number, cellHeight: number): number {
  return Math.max(0, Math.floor(pointerY / cellHeight));
}

/**
 * Given a board and a tile being moved to {x,y}, resolve the final placement:
 * - If no collision: use {x,y} directly.
 * - If collision: find nearest free cell.
 * - If no free cell found: return null (caller should revert).
 */
export function resolveDrop(
  board: Board,
  tileId: string,
  targetX: number,
  targetY: number,
): GridCell | null {
  const tile = board.tiles.find((t) => t.id === tileId);
  if (!tile) return null;
  const { w, h } = sizeToWH(tile.display.size);
  const layout = board.layout as LayoutEntry[];

  if (!hasCollision(layout, targetX, targetY, w, h, tileId)) {
    return { x: targetX, y: targetY };
  }
  return findNearestFreeCell(layout, targetX, targetY, w, h, tileId, board.grid.cols);
}

/**
 * Find the first free cell for a new tile with the given footprint.
 * Scans row-by-row, left-to-right, returning the first gap.
 */
export function findAddPosition(
  board: Board,
  w: number,
  h: number,
): GridCell {
  const layout = board.layout as LayoutEntry[];
  const cols = board.grid.cols;
  // Try rows 0..99
  for (let y = 0; y < 100; y++) {
    for (let x = 0; x <= cols - w; x++) {
      if (!hasCollision(layout, x, y, w, h, null)) {
        return { x, y };
      }
    }
  }
  // Fallback: append below everything
  const maxY = layout.reduce((m, e) => Math.max(m, e.y + e.h), 0);
  return { x: 0, y: maxY };
}

/**
 * Compute CSS pixel displacements for neighbor tiles during a live drag preview.
 *
 * Places the dragged tile speculatively at (hoverX, hoverY) — the current pointer
 * cell — without collision checking for the dragged tile itself. For each neighbor
 * tile whose footprint overlaps the dragged tile's hover footprint, finds the nearest
 * free cell for that neighbor (excluding the dragged tile and already-placed
 * speculative tiles). Returns pixel deltas as `(newCol - currentCol) * cellWidth`
 * and `(newRow - currentRow) * cellHeight` for each displaced tile.
 *
 * Callers apply these as `transform: translate(dx, dy)` CSS transforms (no DOM reorder).
 * Returns an empty map for unknown dragTileId or out-of-bounds hover.
 *
 * Pure: no side-effects, no mutations.
 */
export function computeDisplacements(
  board: Board,
  dragTileId: string,
  hoverX: number,
  hoverY: number,
  cellWidth: number,
  cellHeight: number,
): ReadonlyMap<string, { readonly dx: number; readonly dy: number }> {
  const dragTile = board.tiles.find((t) => t.id === dragTileId);
  if (!dragTile) return new Map();

  const { w: dragW, h: dragH } = sizeToWH(dragTile.display.size);

  // Clamp hover position to valid grid bounds.
  const clampedX = Math.max(0, Math.min(board.grid.cols - dragW, hoverX));
  const clampedY = Math.max(0, hoverY);

  // Build speculative layout: dragged tile placed at hover cell (ignoring its own
  // collision — the user is actively holding it there). Neighbors that overlap its
  // new footprint are relocated to their nearest free cell.
  const speculativeLayout: LayoutEntry[] = [];
  speculativeLayout.push({ tileId: dragTileId, x: clampedX, y: clampedY, w: dragW, h: dragH });

  for (const entry of board.layout as LayoutEntry[]) {
    if (entry.tileId === dragTileId) continue;

    // Does this neighbor overlap the dragged tile's hover footprint?
    const overlapsDragged = !(
      entry.x + entry.w <= clampedX ||
      entry.x >= clampedX + dragW ||
      entry.y + entry.h <= clampedY ||
      entry.y >= clampedY + dragH
    );

    if (overlapsDragged) {
      // Neighbor must move. Find nearest free cell given already-placed speculative tiles.
      const freeCell = findNearestFreeCell(
        speculativeLayout,
        entry.x,
        entry.y,
        entry.w,
        entry.h,
        null,
        board.grid.cols,
      );
      if (freeCell) {
        speculativeLayout.push({ tileId: entry.tileId, x: freeCell.x, y: freeCell.y, w: entry.w, h: entry.h });
      } else {
        speculativeLayout.push({ ...entry });
      }
    } else {
      speculativeLayout.push({ ...entry });
    }
  }

  // Compute pixel deltas for tiles that moved.
  const result = new Map<string, { readonly dx: number; readonly dy: number }>();
  for (const specEntry of speculativeLayout) {
    if (specEntry.tileId === dragTileId) continue;
    const currentEntry = (board.layout as LayoutEntry[]).find((e) => e.tileId === specEntry.tileId);
    if (!currentEntry) continue;
    const dx = (specEntry.x - currentEntry.x) * cellWidth;
    const dy = (specEntry.y - currentEntry.y) * cellHeight;
    if (dx !== 0 || dy !== 0) {
      result.set(specEntry.tileId, { dx, dy });
    }
  }
  return result;
}
